/**
 * roleHome.ts — where each role's own portal lives, and how to name it.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C1.7 (portal redesign).
 *
 * WHY: both the post-login redirect (AuthPage) and the wrong-role screen
 * (RequireRole) need to answer "where does this principal actually belong?" —
 * kept in one place so the two surfaces never drift.
 */
import type { UserRole } from '../api/types';

/** Standalone /auth register waits here until the emailed verify link is followed. */
export const CHECK_EMAIL_PATH = '/auth?mode=check-email';

/** Where a signed-in principal's own portal lives. */
export function homeForRole(role: UserRole): string {
  return role === 'CLINICIAN' ? '/clinicians' : role === 'ADMIN' ? '/admin' : '/patients';
}

/**
 * Where to send someone after login/register. An unverified PATIENT must confirm
 * the emailed link before the dashboard — journey checkout (OTP) already sets
 * emailVerified and skips this. Staff are verified at invite-accept.
 */
export function destinationAfterAuth(
  me: { role: UserRole; emailVerified: boolean },
  next: string | null = null,
): string {
  if (me.role === 'PATIENT' && !me.emailVerified) return CHECK_EMAIL_PATH;
  return safeNextForRole(next, me.role) ?? homeForRole(me.role);
}

/** Role-appropriate CTA copy for a "go to your own area" link. */
export function homeLabelForRole(role: UserRole): string {
  if (role === 'CLINICIAN') return 'Go to the clinician workspace';
  if (role === 'ADMIN') return 'Go to the admin console';
  return 'Go to your dashboard';
}

/**
 * Validate a `?next=` redirect target against the signed-in role. Returns
 * `next` only when it is an INTERNAL path ('/'-prefixed but not '//', which a
 * browser treats as protocol-relative → an open redirect) AND role-compatible:
 * '/admin…' requires ADMIN, '/clinicians…' requires CLINICIAN; every other
 * internal path is open to any role. Anything else → null, so the caller
 * falls back to `homeForRole` — a crafted `?next=/admin` can never steer a
 * patient toward a staff surface, and an external URL is never followed.
 */
export function safeNextForRole(next: string | null, role: UserRole): string | null {
  if (!next || !next.startsWith('/') || next.startsWith('//')) return null;
  if (next.startsWith('/admin') && role !== 'ADMIN') return null;
  if (next.startsWith('/clinicians') && role !== 'CLINICIAN') return null;
  return next;
}
